import { InformationCircleIcon } from '@heroicons/react/24/outline'
import Input from '@/components/common/Input'

interface FeedbackLoopConfigProps {
  enabled: boolean
  maxIterations?: number
  onEnabledChange: (enabled: boolean) => void
  onMaxIterationsChange: (maxIterations: number) => void
  disabled?: boolean
}

const FeedbackLoopConfig = ({
  enabled,
  maxIterations = 3,
  onEnabledChange,
  onMaxIterationsChange,
  disabled = false,
}: FeedbackLoopConfigProps) => {
  return (
    <div className="space-y-4">
      <div className="flex items-start gap-3">
        <input
          type="checkbox"
          id="feedback-enabled"
          checked={enabled}
          onChange={(e) => onEnabledChange(e.target.checked)}
          disabled={disabled}
          className="mt-1 w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
        />
        <div className="flex-1">
          <label htmlFor="feedback-enabled" className="block text-sm font-medium text-gray-900">
            Enable Feedback Loop
          </label>
          <p className="text-sm text-gray-600 mt-1">
            Allow agents to iterate until validation passes or max iterations reached
          </p>
        </div>
      </div>

      {enabled && (
        <div className="ml-7 p-4 bg-blue-500/10 border border-blue-500/20 rounded-lg space-y-4">
          <div className="flex items-start gap-2">
            <InformationCircleIcon className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
            <div className="text-sm text-blue-600">
              <p className="font-medium mb-1">How Feedback Loops Work:</p>
              <ol className="list-decimal list-inside space-y-1 text-blue-600">
                <li>Worker agents perform their tasks</li>
                <li>Validator agents review the output</li>
                <li>If validation fails, feedback is sent back to workers</li>
                <li>Workers refine their output based on feedback</li>
                <li>Process repeats until validation passes or max iterations reached</li>
              </ol>
            </div>
          </div>

          <div className="pt-2">
            <Input
              label="Maximum Iterations"
              type="number"
              value={maxIterations}
              onChange={(e) => onMaxIterationsChange(parseInt(e.target.value) || 3)}
              min={1}
              max={100}
              disabled={disabled}
              helperText="Number of times the team will iterate before stopping (1-100)"
              required={enabled}
            />
          </div>

          <div className="text-xs text-blue-600 space-y-1">
            <p>
              <strong>Recommended:</strong> 3-5 iterations for most use cases
            </p>
            <p>
              <strong>Note:</strong> Each iteration incurs API costs. Set appropriately to balance
              quality and cost.
            </p>
          </div>
        </div>
      )}
    </div>
  )
}

export default FeedbackLoopConfig
